import { useState, useCallback } from "react";
import { Upload, FileText, X } from "lucide-react";
import { toast } from "sonner";
import { extractTextFromFile } from "@/lib/cvExtractor";

interface CVUploaderProps {
  onTextExtracted: (text: string) => void;
  onPasteStart?: () => void;
}

const CVUploader = ({ onTextExtracted }: CVUploaderProps) => {
  const isMobile = /iPhone|iPad|Android/i.test(navigator.userAgent);
  const [file, setFile] = useState<File | null>(null);
  const [showPaste, setShowPaste] = useState(isMobile);
  const [pasteText, setPasteText] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [extracting, setExtracting] = useState(false);

  const extractText = useCallback(async (f: File) => {
    setExtracting(true);
    try {
      const text = await extractTextFromFile(f);
      onTextExtracted(text);
    } catch (e: any) {
      console.error("Extraction error:", e);
      if (isMobile) {
        toast.error("PDF upload is not supported on mobile. Please paste your CV manually below.");
        setShowPaste(true);
      } else {
        toast.error(e.message || "Failed to extract text from file");
      }
    } finally {
      setExtracting(false);
    }
  }, [onTextExtracted, isMobile]);

  const handleFile = useCallback((f: File) => {
    if (!f.name.endsWith(".pdf") && !f.name.endsWith(".docx")) return;
    setFile(f);
    setShowPaste(false);
    extractText(f);
  }, [extractText]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  }, [handleFile]);

  return (
    <div className="space-y-3">
      {isMobile && !showPaste && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mb-4">
          <p className="text-[10px] text-amber-500 font-bold uppercase tracking-widest text-center">
            Mobile Notice: PDF upload is unreliable. Manual paste is recommended.
          </p>
        </div>
      )}

      {!showPaste ? (
        <>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => document.getElementById("cv-file-input")?.click()}
            className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all duration-200 ${
              dragOver ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
            }`}
          >
            <input
              id="cv-file-input"
              type="file"
              accept=".pdf,.docx"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
            />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileText className="w-6 h-6 text-primary" />
                <span className="text-sm font-medium">{file.name}</span>
                {extracting && <span className="text-xs text-muted-foreground">(extracting...)</span>}
                <button onClick={(e) => { e.stopPropagation(); setFile(null); onTextExtracted(""); }} className="text-muted-foreground hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="font-heading font-semibold text-sm mb-1">Upload Your CV</p>
                <p className="text-xs text-muted-foreground">PDF or .docx — drag & drop or click</p>
              </>
            )}
          </div>
          <button
            onClick={() => {
              setShowPaste(true);
              if (onPasteStart) onPasteStart();
            }}
            className="text-xs text-muted-foreground hover:text-primary transition-colors block mx-auto mt-4 md:mt-2"
          >
            {extracting ? "Loading..." : "Or paste your CV text manually"}
          </button>
        </>
      ) : (
        <>
          <textarea
            value={pasteText}
            onChange={(e) => { setPasteText(e.target.value); onTextExtracted(e.target.value); }}
            placeholder="Paste your CV text here..."
            className="w-full h-48 bg-secondary/50 border border-border rounded-xl p-4 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary placeholder:text-muted-foreground"
          />
          {!isMobile && (
            <button
              onClick={() => { setShowPaste(false); setPasteText(""); }}
              className="text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              or upload a file instead
            </button>
          )}
          {isMobile && (
            <p className="text-[10px] text-muted-foreground text-center uppercase tracking-widest mt-2">
              Paste your CV text above to get started
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default CVUploader;
